/**
 * useHomeNeedsCount — the header's Home badge (plan 443, pmh-badge).
 *
 * The badge is `buildNeedsStream(...).count`: the same function Home's Needs
 * list is built from (both read `useHomeNeeds`), fed by the same stores
 * (visible Today queue, the pending requests list, the Home summary), so the
 * two cannot disagree. Readiness-moved, timing, drafts and snoozed items are
 * excluded by the stream itself.
 *
 * The pipeline summary is Home-only, so the badge (mounted on every route)
 * does not load it.
 *
 * Marketplace inputs load only while the go-live gate is `on`; with it `off` or
 * `loading` the requests list and the Home summary are `null` and no
 * marketplace request is issued. CRM rows (new buyers, high-intent signals,
 * follow-ups) still count.
 */
import { useMemo } from "react";
import type { MarketplaceGate } from "./useMarketplaceGate";
import { useHomeNeeds } from "./useHomeNeeds";

export interface HomeNeedsCount {
  /** `buildNeedsStream(...).count`; 0 hides the badge. */
  homeNeeds: number;
  /** Pending marketplace requests (the Marketplace badge). */
  marketplaceRequests: number;
}

export function useHomeNeedsCount(gate: MarketplaceGate): HomeNeedsCount {
  const { count, requests } = useHomeNeeds(gate, { pipeline: false });
  const marketplaceRequests = requests?.length ?? 0;
  return useMemo(
    () => ({ homeNeeds: count, marketplaceRequests }),
    [count, marketplaceRequests],
  );
}
